/**
 * openable-refs: untrusted HTML (preview snapshots, .html files) rendered on the cross-site
 * artifact origin, never the app origin (S05). The frame is a static usercontent page
 * (`/frame/snapshot`, opaque origin, no network) that announces itself; the decrypted HTML is
 * then posted into that exact window. The hosted Worker never receives the content.
 */
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

const ORIGIN = (import.meta.env.VITE_VIKO_ARTIFACT_ORIGIN as string | undefined) ?? ''

export function SnapshotFrame({
  html,
  title,
  origin,
}: {
  html: string
  title: string
  origin: string
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const win = frame.current?.contentWindow
      if (!win || e.source !== win) return
      if ((e.data as { type?: unknown } | null)?.type !== 'viko-snapshot-ready') return
      // The frame is opaque-origin (sandboxed), so no narrower target origin exists; the
      // recipient is pinned by identity to the window we created.
      win.postMessage({ html }, '*')
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [html])
  return (
    <iframe
      key={html.length}
      ref={frame}
      className="vk-refframe"
      title={title}
      src={`${origin}/frame/snapshot`}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
    />
  )
}

/** Viewer-context renderer when an artifact origin is configured (production), else undefined. */
export function snapshotRenderer(
  origin = ORIGIN,
): ((o: { html: string; title: string }) => ReactNode) | undefined {
  if (!/^https?:\/\//.test(origin)) return undefined
  return ({ html, title }) => <SnapshotFrame html={html} title={title} origin={origin} />
}
